import { z } from "zod";
import { SIGN_IN_METHOD_LABELS, signInMethodSchema, type SignInMethod } from "./signInMethod";

/**
 * Multi-factor sign-in for admins (docs/research/2026-09-28-design-mfa.md, owner answers of
 * 2026-09-28). Shared by the server's gate, the platform settings catalog and the client.
 *
 * Two locks: the identity provider asks for the second factor (a Conditional Access policy), and
 * the server checks the proof in the token's `amr` before an admin action. A browser the person
 * chose to remember after a proven sign-in counts as the proof for `auth.mfa_remember_days`.
 */

/**
 * Who must present a sign-in that proved a second factor, and when (design 4.1):
 * - `off`: nobody.
 * - `privileged`: anyone, on the privileged routes (`PRIVILEGED_ROUTES`).
 * - `admins`: holders of PlatformAdmin, TenantAdmin or InstanceAdmin on every request, and
 *   anyone on the privileged routes.
 * - `all`: everyone on every request.
 */
export const MFA_MODES = ["off", "privileged", "admins", "all"] as const;
export type MfaMode = (typeof MFA_MODES)[number];
export const mfaModeSchema = z.enum(MFA_MODES);

/** Roles that make a person an admin for mode `admins` (design 4.1); the server's gate reads it. */
export const MFA_ADMIN_ROLES = ["PlatformAdmin", "TenantAdmin", "InstanceAdmin"] as const;

/**
 * Whether the mode can ask this person for a second step (round 6 walk W21: the Users grid said
 * "No MFA" on every user while the mode was Off). `all` covers everyone; `admins` the holders of
 * an admin role; `privileged` too, because the privileged routes are admin writes (access,
 * destination, settings) that only those roles reach; `off` nobody.
 */
export function mfaModeCovers(mode: MfaMode, roles: readonly string[]): boolean {
  if (mode === "off") return false;
  if (mode === "all") return true;
  return roles.some((role) => (MFA_ADMIN_ROLES as readonly string[]).includes(role));
}

/**
 * #1712 (owner decision 2026-10-21, Q24 option A): while the mode covers admins
 * (`mfaModeCovers`), an admin role is held only by a person whose sign-in method can prove a
 * second factor (its issuer entry declares `mfa`). Admins sign in with single sign-on, so a grant,
 * role change, restore, reactivation or Reset sign-in that would leave an admin role on any other
 * method is refused, and every refusal leads with this reason. The rule reads the issuer's
 * declaration, never a provider name, so another provider (Google, Okta) qualifies once its entry
 * declares it, and the sentences below name the choices from that same list.
 */
export const ADMIN_NEEDS_SSO_REASON =
  "For security, admin roles need single sign-on with a work account.";

/** #1712: no sign-in this deployment offers can hold an admin role yet. */
export const ADMIN_NEEDS_SSO_UNAVAILABLE_MESSAGE = `${ADMIN_NEEDS_SSO_REASON} No sign-in method here can hold an admin role yet. Ask Skypoint support to set up single sign-on.`;

/** "A", "A or B", "A, B or C". */
function orList(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}`;
}

/**
 * #1712: the reason and the choices that can hold an admin role, named as the reader's screen
 * names them (the grant dialog's methods, the Reset sign-in dialog's providers). None left reads
 * `ADMIN_NEEDS_SSO_UNAVAILABLE_MESSAGE`, never a choice that is not there.
 */
export function adminNeedsSsoChoice(names: readonly string[]): string {
  const unique = [...new Set(names)];
  if (unique.length === 0) return ADMIN_NEEDS_SSO_UNAVAILABLE_MESSAGE;
  return `${ADMIN_NEEDS_SSO_REASON} Choose ${orList(unique)}.`;
}

/** #1712: `adminNeedsSsoChoice` over the grant dialog's sign-in methods. */
export function adminNeedsSsoMessage(methods: readonly SignInMethod[]): string {
  return adminNeedsSsoChoice(methods.map((method) => SIGN_IN_METHOD_LABELS[method]));
}

/**
 * #1712: the rule for a person already signed in or waiting through another method. Only a
 * Platform admin can reset a sign-in (`/platform/users/:id/rebind`, `platform.tenants.manage`, the
 * Users page's Platform tab), so a Tenant or Instance admin is told who can.
 */
export const ADMIN_NEEDS_SSO_RESET_MESSAGE = `${ADMIN_NEEDS_SSO_REASON} Use Reset sign-in on the Platform tab of Users to move this person to single sign-on first.`;
export const ADMIN_NEEDS_SSO_ASK_RESET_MESSAGE = `${ADMIN_NEEDS_SSO_REASON} Ask a Skypoint platform admin to reset this person's sign-in to single sign-on first.`;

/** #1712: the reset sentence for a reader who can (`true`) or cannot reset a sign-in. */
export function adminNeedsSsoResetMessage(canResetSignIn: boolean): string {
  return canResetSignIn ? ADMIN_NEEDS_SSO_RESET_MESSAGE : ADMIN_NEEDS_SSO_ASK_RESET_MESSAGE;
}

/**
 * #1712: an admin whose own sign-in cannot prove a second step, at the step-up page. Only a
 * Platform admin can move them, so the sentence says who to ask.
 */
export const ADMIN_OWN_SIGN_IN_NEEDS_SSO_MESSAGE = `${ADMIN_NEEDS_SSO_REASON} Your sign-in cannot prove a second step. Ask a Skypoint platform admin to reset your sign-in to single sign-on.`;

/** Whether a role is one of the admin roles `mfaModeCovers` reads. */
export function isMfaAdminRole(role: string): boolean {
  return (MFA_ADMIN_ROLES as readonly string[]).includes(role);
}

/** Stricter wins: the deployment floor (`AUTH_MFA_FLOOR`) and the setting meet here. */
export function strictestMfaMode(a: MfaMode, b: MfaMode): MfaMode {
  return MFA_MODES.indexOf(a) >= MFA_MODES.indexOf(b) ? a : b;
}

/** The platform settings this feature reads (the catalog is `PLATFORM_SETTINGS`). */
export const MFA_MODE_SETTING_KEY = "auth.mfa_mode";
export const MFA_REMEMBER_DAYS_SETTING_KEY = "auth.mfa_remember_days";
export const ADMIN_SESSION_MAX_HOURS_SETTING_KEY = "auth.admin_session_max_hours";

/**
 * Owner answer W3-9: "Do not ask again on this browser for 14 days", and a fresh sign-in at least
 * every 12 hours for admins. Both are deployment settings; `docs/security/authentication-policy.md`
 * holds the reasoning (NIST SP 800-63B; HIPAA sets no number).
 */
export const DEFAULT_MFA_REMEMBER_DAYS = 14;
export const MAX_MFA_REMEMBER_DAYS = 30;
export const DEFAULT_ADMIN_SESSION_MAX_HOURS = 12;
/** NIST SP 800-63B AAL2: reauthenticate at least once per 12 hours, so the setting cannot exceed it. */
export const MAX_ADMIN_SESSION_MAX_HOURS = 12;

/** The cookie that names a remembered browser. HttpOnly; its value is never logged or stored. */
export const MFA_DEVICE_COOKIE = "skydata_mfa_device";

/** What the server asks the browser to do before anything else works (`GET /me` `signInStep`). */
export const SIGN_IN_STEPS = ["mfa", "reauth"] as const;
export type SignInStep = (typeof SIGN_IN_STEPS)[number];

/** The MFA block of `GET /api/v2/me`. Identifiers and booleans only, never the `amr` values. */
export const meMfaSchema = z.object({
  /** The effective mode: the stricter of the deployment floor and the setting. */
  mode: mfaModeSchema,
  /** This sign-in proved a second factor: through the token, or a remembered browser. */
  proven: z.boolean(),
  /** How it was proved; null when it was not. */
  source: z.enum(["token", "remembered_device"]).nullable(),
  /** The issuer this person signed in through declares that its tokens can prove MFA. */
  issuerCanProve: z.boolean(),
  /** The dev-only exclusion list names this person (`AUTH_DEV_EXCLUSIONS`). */
  excluded: z.boolean(),
  /** How long a remembered browser lasts, in days; 0 when remembering is off. */
  rememberDays: z.number().int().min(0),
  /** How long an admin's sign-in lasts under mode `admins` or `all`, in hours. */
  adminSessionMaxHours: z.number().int().min(1),
  /** The signed-in person's issuer is a work-account (workforce) issuer. */
  workforce: z.boolean(),
  /**
   * B-57: the token says when the person last signed in (`auth_time`). False means the issuer
   * does not send it, so a `reauth` step can never be satisfied by signing in again: the page
   * says so and offers no redirect. Optional for a server from before round 9.
   */
  authTimeKnown: z.boolean().optional(),
  /**
   * #1712: present only while the mode covers admins. The grant dialog's sign-in methods that can
   * hold an admin role (their issuer declares `mfa`); every other method is refused for one.
   * Absent when the mode does not cover admins, so the dialog offers every method as before.
   */
  adminSignInMethods: z.array(signInMethodSchema).optional(),
  /**
   * #1712: true when this person holds an admin role while the mode covers admins and their own
   * sign-in cannot prove a second factor, so the step-up page says the single sign-on reason.
   * Absent otherwise.
   */
  adminNeedsSso: z.boolean().optional(),
});
export type MeMfa = z.infer<typeof meMfaSchema>;

/** `POST /api/v2/auth/mfa/remembered-device` answers this; the cookie itself is HttpOnly. */
export const rememberedDeviceViewSchema = z.object({
  expiresAt: z.string(),
  days: z.number().int().min(1),
});
export type RememberedDeviceView = z.infer<typeof rememberedDeviceViewSchema>;

/** One configured issuer and whether it can prove MFA (the settings screen's read-only list). */
export const mfaIssuerViewSchema = z.object({
  name: z.string().nullable(),
  claims: z.string(),
  canProveMfa: z.boolean(),
});

/** `GET /api/v2/platform/auth/mfa`: what the settings screen shows beside the mode dropdown. */
export const mfaPolicyViewSchema = z.object({
  /** The deployment floor, from `AUTH_MFA_FLOOR`; the UI cannot lower it. */
  floor: mfaModeSchema,
  /** The stored setting, or `off` when it has never been written. */
  setting: mfaModeSchema,
  effective: mfaModeSchema,
  rememberDays: z.number().int(),
  adminSessionMaxHours: z.number().int(),
  issuers: z.array(mfaIssuerViewSchema),
  /** How many addresses the dev-only exclusion list names (never the addresses). */
  exclusions: z.number().int().min(0),
  /**
   * #1712: how many people hold an active admin role through a sign-in that cannot prove a second
   * factor (never who). Once the mode covers admins, each is refused every admin action until a
   * Platform admin resets their sign-in. Optional so an older view still parses.
   */
  adminsWithoutSso: z.number().int().min(0).optional(),
});
export type MfaPolicyView = z.infer<typeof mfaPolicyViewSchema>;
